import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe, StripeElementsOptions } from '@stripe/stripe-js';
import { loadStripeAfterPageLoad } from '../utils/deferredStripe';

type Props = {
  publishableKey: string;
  options: StripeElementsOptions;
  children: React.ReactNode;
  loadClient?: (key: string) => Promise<Stripe | null> | null;
};

/** Own the loader rejection before handing a resolved client to Elements. */
export default function CheckoutStripeElements({
  publishableKey,
  options,
  children,
  loadClient = loadStripeAfterPageLoad,
}: Props) {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    setFailed(false);
    const initialization = loadClient(publishableKey);
    Promise.resolve(initialization).then(
      (client) => {
        if (!active) return;
        if (client) setStripe(client);
        else setFailed(true);
      },
      () => {
        if (active) setFailed(true);
      },
    );
    return () => {
      active = false;
    };
  }, [publishableKey, attempt, loadClient]);

  return (
    <>
      {failed && (
        <div role="alert" className="alert alert-warning">
          <p>The payment form could not load. Check your connection and try again.</p>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Retry payment form
          </button>
        </div>
      )}
      <Elements stripe={stripe} options={options}>
        {children}
      </Elements>
    </>
  );
}
